import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Star, Trophy, CreditCard, Building2, CheckCircle2, XCircle, FileText } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function RenewalForm() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const cancelled = params.get("cancelled") === "1";
  const [info, setInfo] = useState(null);
  const [step, setStep] = useState(() => (params.get("token") ? "loading" : "invalid")); // loading | invalid | already | form | declined | redirect
  const [form, setForm] = useState({ want_renew: true, rating: 5, comment: "", annual_revenue: "", attachment_url: "", payment_method: "card" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    let mounted = true;
    api.get(`/renewal/check?token=${token}`).then((r) => {
      if (!mounted) return;
      setInfo(r.data);
      setStep(r.data.already_paid ? "already" : "form");
    }).catch(() => { if (mounted) setStep("invalid"); });
    return () => { mounted = false; };
  }, [token]);


  const submit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      const payload = { token, want_renew: form.want_renew, rating: form.rating, comment: form.comment };
      if (form.want_renew) {
        if (!form.annual_revenue) { toast.error("Le chiffre d'affaires est obligatoire pour calculer la cotisation."); setSubmitting(false); return; }
        payload.annual_revenue = parseFloat(form.annual_revenue);
        payload.attachment_url = form.attachment_url || "";
        payload.payment_method = form.payment_method;
      }
      const r = await api.post("/renewal/submit", payload);
      if (!form.want_renew) { setStep("declined"); toast.success("Réponse enregistrée. Merci !"); }
      else if (r.data.checkout_url) { setStep("redirect"); window.location.href = r.data.checkout_url; }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally { setSubmitting(false); }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, attachment_url: r.data.url });
      toast.success("Liasse fiscale chargée");
    } catch (err) { toast.error("Erreur chargement"); }
  };

  return (
    <PublicLayout>
      <section className="mx-auto max-w-3xl px-4 py-16">
        {cancelled && <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{t("comp_renewalform_90")}</div>}

        {step === "loading" && <div className="text-center text-neutral-500">{t("comp_renewalform_91")}</div>}
        {step === "invalid" && <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center text-rose-700" data-testid="renewal-invalid"><XCircle className="mx-auto mb-2 h-8 w-8" />{t("comp_renewalform_92")}</div>}
        {step === "already" && <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center text-emerald-700" data-testid="renewal-already"><CheckCircle2 className="mx-auto mb-2 h-8 w-8" />Votre renouvellement {info?.year} pour {info?.company_name} est déjà réglé. Merci !</div>}
        {step === "declined" && <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-6 text-center" data-testid="renewal-declined"><CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-neutral-600" />{t("comp_renewalform_93")}</div>}
        {step === "redirect" && <div className="text-center text-neutral-500">{t("comp_renewalform_94")}</div>}

        {step === "form" && info && (
          <>
            <div className="text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-wine-50"><Trophy className="h-6 w-6 text-wine-600" /></div>
              <h1 className="mt-4 text-4xl font-bold text-navy">Renouvellement {info.year}</h1>
              <p className="mt-2 text-neutral-600"><Building2 className="inline h-4 w-4" /> {info.company_name}</p>
            </div>

            <form onSubmit={submit} className="mt-8 space-y-6 rounded-3xl border border-neutral-100 bg-white p-6 shadow-sm">
              <div>
                <label className="text-sm font-bold text-neutral-900">Souhaitez-vous renouveler votre adhésion pour {info.year} ?</label>
                <div className="mt-3 flex gap-3">
                  <button type="button" onClick={() => setForm({ ...form, want_renew: true })} className={`flex-1 rounded-xl border-2 px-4 py-3 text-sm font-bold transition-all ${form.want_renew ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-neutral-200 bg-white text-neutral-500 hover:border-emerald-300"}`} data-testid="renewal-yes">✓ Oui, je renouvelle</button>
                  <button type="button" onClick={() => setForm({ ...form, want_renew: false })} className={`flex-1 rounded-xl border-2 px-4 py-3 text-sm font-bold transition-all ${!form.want_renew ? "border-rose-400 bg-rose-50 text-rose-700" : "border-neutral-200 bg-white text-neutral-500 hover:border-rose-300"}`} data-testid="renewal-no">✗ Non merci</button>
                </div>
              </div>

              <div>
                <label className="text-sm font-bold text-neutral-900">{t("comp_renewalform_95")}</label>
                <div className="mt-2 flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} className="text-3xl transition-transform hover:scale-110" data-testid={`renewal-star-${n}`}>
                      <Star className={`h-8 w-8 ${form.rating >= n ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-bold text-neutral-900">{t("comp_renewalform_96")}</label>
                <textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={4} placeholder={t("comp_renewalform_97")} className="mt-2 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="renewal-comment" />
              </div>

              {form.want_renew && (
                <>
                  <div className="rounded-xl border border-wine-100 bg-wine-50/30 p-4">
                    <label className="text-sm font-bold text-neutral-900">Chiffre d&apos;affaires annuel HT (€) *</label>
                    <p className="text-xs text-neutral-500">{t("comp_renewalform_98")}</p>
                    <input required type="number" min="0" value={form.annual_revenue} onChange={(e) => setForm({ ...form, annual_revenue: e.target.value })} placeholder="Ex: 500000" className="mt-2 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="renewal-revenue" />
                  </div>

                  <div>
                    <label className="text-sm font-bold text-neutral-900">Liasse fiscale (PDF, optionnel)</label>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadAttachment} className="mt-2 block w-full text-xs" data-testid="renewal-file" />
                    {form.attachment_url && <div className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-700"><FileText className="h-3 w-3" /> {t("comp_renewalform_99")}</div>}
                  </div>

                  <div>
                    <label className="text-sm font-bold text-neutral-900">{t("comp_renewalform_100")}</label>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <label className={`flex cursor-pointer items-center gap-2 rounded-xl border-2 p-3 text-xs transition-all ${form.payment_method === "card" ? "border-wine-500 bg-wine-50" : "border-neutral-200"}`}>
                        <input type="radio" checked={form.payment_method === "card"} onChange={() => setForm({ ...form, payment_method: "card" })} data-testid="pay-card" /> <CreditCard className="h-4 w-4" /> Carte bancaire
                      </label>
                      <label className={`flex cursor-pointer items-center gap-2 rounded-xl border-2 p-3 text-xs transition-all ${form.payment_method === "sepa_debit" ? "border-wine-500 bg-wine-50" : "border-neutral-200"}`}>
                        <input type="radio" checked={form.payment_method === "sepa_debit"} onChange={() => setForm({ ...form, payment_method: "sepa_debit" })} data-testid="pay-sepa" /> Prélèvement SEPA
                      </label>
                    </div>
                  </div>
                </>
              )}

              <button type="submit" disabled={submitting} className="w-full rounded-full bg-wine-600 px-6 py-3 text-sm font-bold text-white hover:bg-wine-700 disabled:opacity-50" data-testid="renewal-submit">
                {submitting ? "Envoi…" : form.want_renew ? "Valider et payer →" : "Envoyer ma réponse"}
              </button>
            </form>
          </>
        )}
      </section>
    </PublicLayout>
  );
}
